import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import apiClient from "../api/apiClient";
import { useNavigate } from "react-router-dom";

const Asignaciones = () => {
  // filtros y datos base
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [entDate, setEntDate] = useState("");
  const [startRoute, setStartRoute] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // camiones/choferes
  const [choferes, setChoferes] = useState([]);
  const [camiones, setCamiones] = useState([]);
  const [chofer, setChofer] = useState(null);
  const [camion, setCamion] = useState(null);

  // entregas y rutas
  const [deliveries, setDeliveries] = useState([]);
  const [rutas, setRutas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const sapUser = JSON.parse(localStorage.getItem("sapUser"));
  const Company = sapUser?.CompanyDB || "";

  // cargar catálogos
  useEffect(() => {
    const loadAll = async () => {
      try {
        const [proRes, chRes, caRes] = await Promise.all([
          apiClient.get("/sales-orders/projects"),
          apiClient.get("/logistica/choferes", { params: { company: Company } }),
          apiClient.get("/logistica/camiones", { params: { company: Company } }),
        ]);

        setProjects((Array.isArray(proRes.data) ? proRes.data : []).map(p => ({ value: p.PrjCode, label: p.PrjName })));
        setChoferes((Array.isArray(chRes.data) ? chRes.data : []).map(p => ({ value: p.Code, label: p.U_NOMBRE })));
        setCamiones((Array.isArray(caRes.data) ? caRes.data : []).map(p => ({ value: p.Code, label: `${p.U_CHAPA} - ${p.U_MODELO}` })));
      } catch (e) {
        console.error(e);
        setError("Error al obtener datos iniciales.");
      }
    };
    loadAll();
  }, [Company]);

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      // Entregas (remisiones creadas) para asignar chofer/camión
      const { data: delData } = await apiClient.get("/logistica/remisiones", {
        params: {
          project: project?.value ?? "",
          start_route: startRoute,
          end_route: startRoute,
          entDate,
          company: Company,
        },
      });
      const arr = Array.isArray(delData) ? delData : [];
      setDeliveries(
        arr.map((d) => ({
          DocEntry: d.DocEntry,
          DocNum: d.DocNum,
          Project: d.Project,
          Gobernacion: d.CardName,
          Institucion: d.Name,
          DocDueDate: d.DocDueDate,
        }))
      );

      // Rutas (para exportar/importar)
      const rutasRes = await apiClient.get("/logistica/rutas", { params: { project: project?.value ?? "" } });
      setRutas(Array.isArray(rutasRes.data) ? rutasRes.data : []);
    } catch (e) {
      console.error(e);
      setError("Error obteniendo datos.");
    } finally {
      setLoading(false);
    }
  };

  // acciones rutas
  const exportRutas = () => {
    if (!rutas.length) { setError("No hay datos para exportar."); return; }
    const ws = XLSX.utils.json_to_sheet(rutas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rutas");
    XLSX.writeFile(wb, "RutasAsignadas.xlsx");
  };

  const onFile = (e) => setFile(e.target.files?.[0] ?? null);

  const uploadRutas = async () => {
    if (!file) { setError("Selecciona un archivo .xlsx"); return; }
    try {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = async (ev) => {
        const data = new Uint8Array(ev.target.result);
        const wb = XLSX.read(data, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet);
        const payload = { Rutas: json.map(r => ({ Code: r["Code"], U_Ruteo: String(r["U_Ruteo"]) })) };
        const resp = await apiClient.patch("/logistica/rutas", payload);
        const results = resp?.data?.results || [];
        const ok = results.filter(r => r.status === "success").length;
        const warn = results.filter(r => r.status === "warning").length;
        const bad = results.filter(r => r.status === "error").length;
        setMessage(`${ok} rutas ok, ${warn} sin cambios, ${bad} errores.`);
        setError("");
        setFile(null);
        await fetchData();
      };
    } catch (e) {
      console.error(e);
      setError("Error al procesar archivo de rutas.");
    }
  };

  // actualizar chofer/camión en entregas filtradas
  const filteredDeliveries = useMemo(() => {
    if (!searchTerm) return deliveries;
    const q = searchTerm.toLowerCase();
    return deliveries.filter(
      (d) => (d.Gobernacion || "").toLowerCase().includes(q) || (d.Project || "").toLowerCase().includes(q)
    );
  }, [deliveries, searchTerm]);

  const updateDeliveries = async () => {
    if (!filteredDeliveries.length) { alert("No hay entregas para actualizar."); return; }
    if (!chofer?.value || !camion?.value) { alert("Selecciona chofer y camión."); return; }
    try {
      setSubmitting(true);
      const updates = filteredDeliveries.map((row) => ({
        DocEntry: row.DocEntry,
        U_CHOFERES: chofer.value,
        U_VEHICULO: camion.value,
        U_Enviar: "SI",
        U_dKmR: 10,
        U_iMotEmiNR: 8,
      }));
      const resp = await apiClient.patch("/inventario/entregas", { orders: updates });
      const results = resp?.data || [];
      const ok = results.filter((r) => r.status === "success").length;
      const bad = results.filter((r) => r.status === "error").length;
      alert(`${ok} entregas actualizadas. ${bad ? bad + " con error." : ""}`);
      await fetchData();
    } catch (e) {
      console.error(e);
      alert("Error al actualizar entregas");
    } finally {
      setSubmitting(false);
    }
  };

  const delCols = [
    { name: "ID Interno", selector: (r) => r.DocEntry, sortable: true, width: "130px" },
    { name: "Nro Documento", selector: (r) => r.DocNum, sortable: true, width: "130px" },
    { name: "Proyecto", selector: (r) => r.Project, sortable: true, width: "180px" },
    { name: "Gobernación", selector: (r) => r.Gobernacion, sortable: true, width: "360px" },
    { name: "Institución", selector: (r) => r.Institucion, sortable: true, width: "360px" },
  ];

  const rutaCols = [
    { name: "ID Registro", selector: (r) => r.Code, sortable: true },
    { name: "ID Escuela", selector: (r) => r.U_CntctCode },
    { name: "Nombre", selector: (r) => r.Name },
    { name: "Ruta", selector: (r) => r.U_Ruteo },
    { name: "Proyecto", selector: (r) => r.U_Project },
  ];

  return (
    <div className="container mt-4">
      <h2>Asignación de Rutas y Camiones</h2>
      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row g-3 align-items-end">
        <div className="col-md-4">
          <label className="form-label">Proyecto</label>
          <Select options={projects} value={project} onChange={setProject} placeholder="Seleccione un proyecto..." isSearchable />
        </div>
        <div className="col-md-2">
          <label className="form-label">Fecha Entrega</label>
          <input type="date" className="form-control" value={entDate} onChange={(e) => setEntDate(e.target.value)} />
        </div>
        <div className="col-md-2">
          <label className="form-label">Ruta</label>
          <input type="number" className="form-control" value={startRoute} onChange={(e) => setStartRoute(e.target.value)} />
        </div>
        <div className="col-md-4">
          <label className="form-label">Buscar</label>
          <input className="form-control" placeholder="Filtrar entregas" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>

      <div className="row g-3 mt-2 align-items-end">
        <div className="col-md-4">
          <label className="form-label">Chofer</label>
          <Select options={choferes} value={chofer} onChange={setChofer} placeholder="Seleccione un chofer..." isSearchable />
        </div>
        <div className="col-md-4">
          <label className="form-label">Camión</label>
          <Select options={camiones} value={camion} onChange={setCamion} placeholder="Seleccione un camión..." isSearchable />
        </div>
        <div className="col-md-4 d-flex gap-2">
          <button className="btn btn-success" onClick={fetchData} disabled={loading}>
            {loading ? "Cargando..." : "Obtener Datos"}
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/logistica/ruteo-pendiente')} disabled={!project || !entDate}>
            Ruteo Pendiente
          </button>
        </div>
      </div>

      <div className="mt-3 d-flex gap-2 flex-wrap">
        <button className="btn btn-dark" onClick={updateDeliveries} disabled={!filteredDeliveries.length || submitting}>
          {submitting ? "Actualizando Entregas..." : "Confirmar y Actualizar Entregas"}
        </button>
        <button className="btn btn-outline-secondary" onClick={exportRutas} disabled={!rutas.length}>
          Exportar Rutas a Excel
        </button>
        <div className="d-flex align-items-center gap-2">
          <input type="file" accept=".xlsx" onChange={onFile} />
          <button className="btn btn-primary" onClick={uploadRutas} disabled={!file}>Subir Rutas</button>
        </div>
      </div>

      <hr />
      <h5 className="mt-2">Entregas</h5>
      <DataTable columns={delCols} data={filteredDeliveries} pagination dense highlightOnHover />

      <h5 className="mt-4">Rutas</h5>
      <DataTable columns={rutaCols} data={rutas} pagination dense />
    </div>
  );
};

export default Asignaciones;
